import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { EventRecord } from "@/shared/domain/events";

import { MetadataForm, MetadataView } from "../../Metadata";
import { RobotoTypography } from "../../RobotoTypography";
import { AutocompleteType } from "../../TagAndMetadataAutocomplete";
import {
  useUpdateWorkspaceEvent,
  useWorkspaceEvent,
} from "../WorkspaceCtx/useWorkspaceEvents";

import styles from "./EventMetadata.module.css";

interface EventMetadataProps {
  eventId: EventRecord["event_id"];
  onError: (error: Error) => void;
}

export function EventMetadata({ eventId, onError }: EventMetadataProps) {
  const eventQuery = useWorkspaceEvent(eventId);
  const updateMetadataMutation = useUpdateWorkspaceEvent();

  const updateFields = (updates: Record<string, unknown>) => {
    updateMetadataMutation
      .mutateAsync({
        eventId,
        request: {
          metadata_changeset: {
            put_fields: updates,
          },
        },
      })
      .catch(onError);
  };
  const addMetadataToEvent = (key: string, value: unknown) => {
    updateFields({ [key]: value });
  };

  const removeFields = (keys: string[]) => {
    updateMetadataMutation
      .mutateAsync({
        eventId,
        request: {
          metadata_changeset: {
            remove_fields: keys,
          },
        },
      })
      .catch(onError);
  };
  const removeMetadataFromEvent = (key: string) => removeFields([key]);

  if (eventQuery.isPending) {
    return (
      <section>
        <RobotoTypography component={"h3"} className={styles.title}>
          Metadata
        </RobotoTypography>
        <MetadataForm
          metadata={{}}
          onAddMetadata={addMetadataToEvent}
          autocompleteType={AutocompleteType.EventMetadataKeys}
        />
      </section>
    );
  }

  if (eventQuery.isError) {
    return (
      <section>
        <RobotoTypography component={"h3"} className={styles.title}>
          Metadata
        </RobotoTypography>

        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <ErrorOutlineIcon color="error" />
          <RobotoTypography>Failed to load event tags</RobotoTypography>
        </div>
      </section>
    );
  }

  const { metadata } = eventQuery.data;

  return (
    <section>
      <RobotoTypography component={"h3"} className={styles.title}>
        Metadata
      </RobotoTypography>

      <MetadataView
        metadata={metadata}
        onDeleteMetadata={removeMetadataFromEvent}
        onChange={(updates, diff) => {
          if (diff?.keysDeleted && diff.keysDeleted.length > 0) {
            removeFields(diff.keysDeleted);
          }
          if (diff?.keysUpdated && diff.keysUpdated.length > 0) {
            const putFields: Record<string, unknown> = {};
            for (const k of diff.keysUpdated) {
              putFields[k] = updates[k];
            }
            updateFields(putFields);
          }
        }}
      />
      <MetadataForm
        metadata={metadata}
        onAddMetadata={addMetadataToEvent}
        autocompleteType={AutocompleteType.EventMetadataKeys}
      />
    </section>
  );
}
